import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { formatDateTime } from "@/lib/format";
import { Loader2, Search, MessagesSquare } from "lucide-react";

export default function AdminChats() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async (query) => {
    const { data } = await api.get("/admin/chat/conversations", { params: { q: query || undefined } });
    setRows(data);
    setLoading(false);
  }, []);

  useEffect(() => { load(""); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
  }, [q, load]);
  useEffect(() => {
    const t = setInterval(() => load(q), 8000);
    return () => clearInterval(t);
  }, [q, load]);

  if (loading) return <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-chat-list">
      <div className="flex items-center gap-2">
        <MessagesSquare className="h-6 w-6 text-brand" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Customer Chats</h1>
          <p className="text-muted-foreground text-sm">All support conversations with your customers.</p>
        </div>
      </div>

      <Card className="p-5 ip-card-shadow">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="chat-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer name, email or application number" className="pl-9" />
        </div>

        {rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No conversations yet.</p>
        ) : (
          <div className="divide-y divide-border/60">
            {rows.map((c) => (
              <button
                key={c.id}
                onClick={() => nav(`/admin/chats/${c.id}`)}
                className="w-full flex items-center gap-3 py-3.5 text-left hover:bg-brand-light/40 rounded-lg px-2 -mx-2 transition-colors"
                data-testid={`chat-row-${c.id}`}
              >
                <div className="h-10 w-10 shrink-0 grid place-items-center rounded-full bg-brand text-white font-bold">
                  {(c.customer_name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold truncate">{c.customer_name || "Customer"}</p>
                    {c.reference_number && <span className="font-mono text-[11px] text-muted-foreground">{c.reference_number}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{c.customer_email}</p>
                  <p className="text-sm text-muted-foreground truncate mt-0.5">{c.last_message || "No messages yet"}</p>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  {c.last_message_at && <span className="text-[11px] text-muted-foreground">{formatDateTime(c.last_message_at)}</span>}
                  {c.unread_admin > 0 && (
                    <span className="rounded-full bg-gold text-white text-xs font-bold px-2.5 py-0.5" data-testid={`chat-unread-${c.id}`}>
                      {c.unread_admin} new
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
